/**
 * Áudio compartilhado da mesa (música de fundo e efeitos).
 *
 * O problema central é sincronizar reprodução entre clientes sem depender de
 * relógios sincronizados. A solução é guardar no servidor apenas o par
 * (posição, instante da medição) e deixar cada cliente calcular a posição atual:
 *
 *     posição agora = posição + (agora - instante da medição)
 *
 * Assim o cliente que entrou no meio da música cai no lugar certo, e o que
 * perdeu a conexão se realinha ao voltar. O `<audio>` local apenas segue essa
 * conta, com correção de desvio quando a diferença fica grande.
 */
import { z } from 'zod';
import { IdSchema } from './domain.js';

export const AudioKindSchema = z.enum(['music', 'sfx']);
export type AudioKind = z.infer<typeof AudioKindSchema>;

/** Só http(s) ou um arquivo da sala (`file:<id>`): nada de `javascript:` e afins. */
export const AudioUrlSchema = z
  .string()
  .min(1)
  .max(2000)
  .regex(/^(https?:\/\/\S+|file:[A-Za-z0-9_-]{1,64})$/, 'use um link http(s) ou um arquivo da sala');

export const AudioTrackSchema = z.object({
  id: IdSchema,
  url: AudioUrlSchema,
  title: z.string().min(1).max(120),
  kind: AudioKindSchema,
  addedBy: IdSchema,
  addedByName: z.string().min(1).max(40),
});
export type AudioTrack = z.infer<typeof AudioTrackSchema>;

export const AudioRepeatSchema = z.enum(['off', 'all', 'one']);
export type AudioRepeat = z.infer<typeof AudioRepeatSchema>;

/**
 * Estado do áudio da mesa. A playlist e o quadro de efeitos ficam guardados (o
 * mestre monta uma vez); "tocando agora" é uma faixa da playlist.
 *
 * `url` de faixa: um endereço http(s) ou `file:<id>` (arquivo enviado para a
 * sala, cujo link assinado o cliente pede na hora de tocar).
 */
export const AudioStateSchema = z.object({
  /** faixa tocando agora (null = nada carregado) */
  current: AudioTrackSchema.nullable(),
  /** posição em segundos no instante `updatedAt` */
  positionSeconds: z.number().min(0).max(86_400),
  isPlaying: z.boolean(),
  /** relógio do servidor no momento em que a posição foi medida */
  updatedAt: z.number().int().nonnegative(),
  /** músicas da mesa, na ordem de reprodução */
  playlist: z.array(AudioTrackSchema).max(200),
  /** quadro de efeitos sonoros */
  pads: z.array(AudioTrackSchema).max(60),
  /** volume da mesa, definido pelo mestre (0 a 1) */
  volume: z.number().min(0).max(1),
  repeat: AudioRepeatSchema,
});
export type AudioState = z.infer<typeof AudioStateSchema>;

export const AudioSfxEventSchema = z.object({
  id: IdSchema,
  url: AudioUrlSchema,
  title: z.string().min(1).max(120),
  /** quem disparou */
  userId: IdSchema,
  displayName: z.string().min(1).max(40),
  at: z.number().int().nonnegative(),
});
export type AudioSfxEvent = z.infer<typeof AudioSfxEventSchema>;

export const AUDIO_LIMITS = {
  /** duração máxima considerada ao posicionar uma faixa (s) */
  maxPositionSeconds: 86_400,
  /** desvio tolerado antes de o cliente corrigir a posição (s) */
  driftToleranceSeconds: 0.75,
  /** faixa de volume aceita do mestre */
  maxVolume: 1,
} as const;

export function emptyAudioState(now = Date.now()): AudioState {
  return {
    current: null,
    positionSeconds: 0,
    isPlaying: false,
    updatedAt: now,
    playlist: [],
    pads: [],
    volume: 1,
    repeat: 'all',
  };
}

/** Comandos de quem controla o áudio (os mestres). */
export type AudioCommand =
  | { type: 'add'; track: AudioTrack }
  | { type: 'remove'; trackId: string }
  | { type: 'clear' }
  | { type: 'play' }
  | { type: 'pause' }
  | { type: 'stop' }
  | { type: 'next' }
  | { type: 'prev' }
  | { type: 'seek'; positionSeconds: number }
  | { type: 'select'; trackId: string }
  /** a faixa acabou num cliente; só avança se ainda for a faixa tocando */
  | { type: 'ended'; trackId: string }
  | { type: 'volume'; volume: number }
  | { type: 'repeat'; repeat: AudioRepeat };

export type AudioResult = { ok: true; state: AudioState; changed: boolean } | { ok: false; error: 'not-found' | 'bad-message' | 'full' };

/**
 * Aplica um comando ao estado. Puro: o servidor guarda e repassa o resultado.
 * `changed: false` = nada a avisar (por exemplo, um "acabou" repetido).
 */
export function applyAudioCommand(state: AudioState, cmd: AudioCommand, now: number): AudioResult {
  const done = (next: AudioState): AudioResult => ({ ok: true, state: next, changed: true });
  const position = audioPositionSeconds(state, now);
  const start = (track: AudioTrack | null, playing: boolean): AudioState => ({ ...state, current: track, positionSeconds: 0, isPlaying: playing && track !== null, updatedAt: now });
  const index = state.current ? state.playlist.findIndex((t) => t.id === state.current!.id) : -1;
  switch (cmd.type) {
    case 'add': {
      if (cmd.track.kind === 'sfx') {
        if (state.pads.length >= 60) return { ok: false, error: 'full' };
        return done({ ...state, pads: [...state.pads, cmd.track] });
      }
      if (state.playlist.length >= 200) return { ok: false, error: 'full' };
      const playlist = [...state.playlist, cmd.track];
      // a primeira música vira "tocando agora" (pausada)
      return done(state.current ? { ...state, playlist } : { ...start(cmd.track, false), playlist });
    }
    case 'remove': {
      if (state.pads.some((t) => t.id === cmd.trackId)) return done({ ...state, pads: state.pads.filter((t) => t.id !== cmd.trackId) });
      const at = state.playlist.findIndex((t) => t.id === cmd.trackId);
      if (at < 0) return { ok: false, error: 'not-found' };
      const playlist = state.playlist.filter((t) => t.id !== cmd.trackId);
      if (state.current?.id !== cmd.trackId) return done({ ...state, playlist });
      // tirou a que tocava: passa para a que estava depois dela
      const following = playlist[at] ?? (state.repeat === 'all' ? playlist[0] : undefined) ?? null;
      return done({ ...start(following, state.isPlaying), playlist });
    }
    case 'clear':
      return done({ ...start(null, false), playlist: [] });
    case 'play':
      if (!state.current) return { ok: false, error: 'bad-message' };
      // retoma de onde estaria agora, não de onde estava quando pausou
      return done({ ...state, positionSeconds: position, isPlaying: true, updatedAt: now });
    case 'pause':
      return done({ ...state, positionSeconds: position, isPlaying: false, updatedAt: now });
    case 'stop':
      return done({ ...state, positionSeconds: 0, isPlaying: false, updatedAt: now });
    case 'seek':
      if (!state.current) return { ok: false, error: 'bad-message' };
      return done({ ...state, positionSeconds: clampPosition(cmd.positionSeconds), updatedAt: now });
    case 'select': {
      const track = state.playlist.find((t) => t.id === cmd.trackId);
      if (!track) return { ok: false, error: 'not-found' };
      return done(start(track, true));
    }
    case 'next':
    case 'prev': {
      if (!state.playlist.length) return done(start(null, false));
      const step = cmd.type === 'next' ? 1 : -1;
      const n = state.playlist.length;
      const target = index < 0 ? 0 : (index + step + n) % n;
      return done(start(state.playlist[target]!, state.isPlaying));
    }
    case 'ended': {
      if (!state.isPlaying || state.current?.id !== cmd.trackId) return { ok: true, state, changed: false };
      if (state.repeat === 'one') return done(start(state.current, true));
      const following = state.playlist[index + 1] ?? (state.repeat === 'all' ? state.playlist[0] : undefined);
      // acabou a lista sem repetir: fica parada no começo da última
      return done(following ? start(following, true) : { ...state, positionSeconds: 0, isPlaying: false, updatedAt: now });
    }
    case 'volume':
      return done({ ...state, volume: Math.min(1, Math.max(0, cmd.volume)) });
    case 'repeat':
      return done({ ...state, repeat: cmd.repeat });
  }
}

/**
 * Posição atual da faixa, calculada a partir do relógio do observador.
 * Nunca decresce e nunca passa do limite considerado válido.
 */
export function audioPositionSeconds(state: AudioState, now: number): number {
  if (!state.current) return 0;
  if (!state.isPlaying) return clampPosition(state.positionSeconds);
  const elapsed = Math.max(0, (now - state.updatedAt) / 1000);
  return clampPosition(state.positionSeconds + elapsed);
}

export function clampPosition(seconds: number): number {
  if (!Number.isFinite(seconds) || seconds < 0) return 0;
  return Math.min(AUDIO_LIMITS.maxPositionSeconds, seconds);
}

/** Diferença entre onde o áudio local está e onde deveria estar. */
export function audioDriftSeconds(expected: number, actual: number): number {
  return expected - actual;
}

/** O desvio é grande o suficiente para justificar um salto na reprodução? */
export function shouldCorrectDrift(expected: number, actual: number): boolean {
  return Math.abs(audioDriftSeconds(expected, actual)) > AUDIO_LIMITS.driftToleranceSeconds;
}
